import {keepPreviousData, useInfiniteQuery} from '@tanstack/react-query';

import {DISCOVER_PAGE_SIZE} from '../../constants/feed';
import apiService from '../../transport/apiService';
import {logger} from '../../utils/logger';

import {filterByDatesPage} from './filterByDates';
import {FeedDiscoverPage, UseFeedDiscoverListParams} from './types';

export const ALL_SEGMENT_VALUE = 'ALL';

/**
 * Cache key shape: ['feed', 'discover', 'v3', storeNumber, storeState, segment].
 *
 * Segment is part of the key so each tab gets its own paginated cache entry.
 * Switching tabs hits a distinct entry (X-Cache HIT on the shared server
 * snapshot when the entry was previously visited within gcTime).
 *
 * `segment = ALL_SEGMENT_VALUE` is the unfiltered universe — the query
 * function omits the `segment` param so the server returns the unfiltered
 * paginated envelope.
 */
export const feedDiscoverListQueryKey = (
  storeNumber: string | undefined,
  storeState: string | undefined,
  segment: string,
) => ['feed', 'discover', 'v3', storeNumber, storeState, segment] as const;

/**
 * Shared query function — also called from prefetch sites in
 * AppNavigationContainer. Fetches one paginated page and returns the
 * date-filtered FeedDiscoverPage. ALL segment omits the `segment` param so
 * the server returns the unfiltered slice.
 */
export const feedDiscoverListQueryFn = async (
  storeNumber: string,
  storeState: string,
  segment: string,
  pageParam: number,
): Promise<FeedDiscoverPage> => {
  const params = {
    offset: pageParam,
    limit: DISCOVER_PAGE_SIZE,
    ...(segment !== ALL_SEGMENT_VALUE ? {segment} : {}),
  };
  const envelope = await apiService.getFeedList(storeNumber, storeState, params);
  const page = filterByDatesPage(envelope);

  logger.info('useFeedDiscoverList', 'Feed page loaded', {
    segment,
    offset: page.offset,
    limit: page.limit,
    total: page.total,
    hasMore: page.hasMore,
    itemCount: page.items.length,
    discoverCount: page.discoverList.length,
  });

  return page;
};

/**
 * Paginated discover-tab feed hook. One cache entry per
 * `(storeNumber, storeState, segment)` tuple. Pages append via
 * `fetchNextPage`; the loop terminates when `getNextPageParam` returns
 * `undefined` (no further `hasMore`).
 *
 * `placeholderData: keepPreviousData` keeps the prior segment's items
 * visible during a tab switch, preventing a blank flash.
 *
 * No `maxPages` cap. This is a forward-only infinite list (no
 * `getPreviousPageParam`), so a `maxPages` window would evict the earliest
 * pages on `fetchNextPage` and never re-fetch them — silently dropping items
 * the user already scrolled past (DIS-1306: "What's New" items vanished from
 * ALL once it grew past 10 pages). The set stays bounded the right way: the
 * middleware date-filters before pagination, so a segment exhausts in a few
 * dense pages rather than ~14 sparse ones.
 */
export const useFeedDiscoverList = ({
  storeNumber,
  storeState,
  segment,
}: UseFeedDiscoverListParams) =>
  useInfiniteQuery({
    queryKey: feedDiscoverListQueryKey(storeNumber, storeState, segment),
    queryFn: ({pageParam}) =>
      feedDiscoverListQueryFn(storeNumber as string, storeState as string, segment, pageParam),
    initialPageParam: 0,
    getNextPageParam: lastPage => (lastPage.hasMore ? lastPage.offset + lastPage.limit : undefined),
    enabled: !!storeNumber && !!storeState,
    placeholderData: keepPreviousData,
  });
